import { useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, runTransaction } from 'firebase/firestore';
import {
  GRID_W, GRID_H, INITIAL_CELLS, MAX_ATTACKS_PER_TURN, WINNING_PERCENTAGE, MAX_TURNS,
  POWER_CONSTANTS,
} from '../constants/gameConstants';
import { useSound } from './useSound';

export const useBotLogic = (roomCode, gameState, players, myPlayerIndex, addBattle) => {
  const botTimeoutRef = useRef(null);
  const isProcessingRef = useRef(false);
  const { playSound } = useSound();

  // ===== HELPER FUNCTIONS =====
  const getIndex = useCallback((x, y) => y * GRID_W + x, []);

  const isValidCoord = useCallback((x, y) => {
    return x >= 0 && x < GRID_W && y >= 0 && y < GRID_H;
  }, []);

  const rollDice = useCallback(() => Math.floor(Math.random() * 6) + 1, []);

  const calculatePowerBonus = useCallback((botIndex, targetOwnerIndex, gs) => {
    const botStats = gs.playerStats?.[botIndex] || { attackPower: 0, defensePower: 0 };
    const targetStats = gs.playerStats?.[targetOwnerIndex] || { attackPower: 0, defensePower: 0 };

    return {
      attackBonus: botStats.attackPower * POWER_CONSTANTS.ATTACK_BONUS_MULTIPLIER,
      defenseBonus: targetStats.defensePower * POWER_CONSTANTS.DEFENSE_BONUS_MULTIPLIER
    };
  }, []);

  const updateBotStatsAfterBattle = useCallback((botIndex, defenderIndex, attackScore, defenseScore, currentStats) => {
    const newStats = { ...currentStats };

    if (!newStats[botIndex]) {
      newStats[botIndex] = { attackPower: 0, defensePower: 0, totalScore: 0 };
    }
    if (!newStats[defenderIndex]) {
      newStats[defenderIndex] = { attackPower: 0, defensePower: 0, totalScore: 0 };
    }

    newStats[botIndex].totalScore += attackScore;
    newStats[defenderIndex].totalScore += defenseScore;

    const atkScore = newStats[botIndex].totalScore;
    const defScore = newStats[defenderIndex].totalScore;

    const MAX_ATTACK_POWER = POWER_CONSTANTS.MAX_ATTACK_POWER;
    const MAX_DEFENSE_POWER = POWER_CONSTANTS.MAX_DEFENSE_POWER;

    newStats[botIndex].attackPower = Math.min(
      MAX_ATTACK_POWER,
      Math.floor(Math.log2(atkScore + 1) / POWER_CONSTANTS.ATTACK_POWER_DIVIDER)
    );

    newStats[defenderIndex].defensePower = Math.min(
      MAX_DEFENSE_POWER,
      Math.floor(Math.log2(defScore + 1) / POWER_CONSTANTS.DEFENSE_POWER_DIVIDER)
    );

    return newStats;
  }, []);

  const checkWinningConditions = useCallback((newGrid, currentPlayers) => {
    const totalCells = GRID_W * GRID_H;
    const territories = currentPlayers.map((_, i) =>
      newGrid.filter(c => c === i).length
    );

    const activePlayers = territories.filter(count => count > 0).length;
    if (activePlayers === 1) {
      const winnerIndex = territories.findIndex(count => count > 0);
      if (winnerIndex !== -1) {
        return winnerIndex;
      }
    }

    const maxTerritory = Math.max(...territories);
    if (maxTerritory >= totalCells * WINNING_PERCENTAGE) {
      return territories.indexOf(maxTerritory);
    }
    return null;
  }, []);

  // ===== SONRAKI AKTİF OYUNCU BULMA =====
  const findNextActivePlayer = useCallback((currentIndex, grid, playerCount) => {
    let nextIndex = (currentIndex + 1) % playerCount;
    let attempts = 0;

    while (attempts < playerCount) {
      const territoryCount = grid.filter(cell => cell === nextIndex).length;
      if (territoryCount > 0) {
        return nextIndex;
      }
      nextIndex = (nextIndex + 1) % playerCount;
      attempts++;
    }

    return 0;
  }, []);

  // ===== BOT SALDIRI (ÖNCE TANIMLANDI) =====
  const botAttack = useCallback(async (roomCode, room, botIndex, attack, gs) => {
    const roomRef = doc(db, 'rooms', roomCode);

    const attackRoll = rollDice();
    const defenseRoll = rollDice();
    const botPrediction = rollDice();
    const defenderPrediction = Math.floor(Math.random() * 6) + 1;

    playSound('attack', 0.6);

    let botScore = 6 - Math.abs(botPrediction - attackRoll);
    let defenderScore = 6 - Math.abs(defenderPrediction - defenseRoll);

    botScore += 0.3;

    const defenderIndex = attack.owner;
    const powerBonus = calculatePowerBonus(botIndex, defenderIndex, gs);

    botScore += powerBonus.attackBonus;
    defenderScore += powerBonus.defenseBonus;

    const luckVariance = (Math.random() - 0.5) * 2;
    botScore += luckVariance;
    defenderScore -= luckVariance / 2;

    const critRoll = Math.random();
    let criticalHit = false;

    if (critRoll > 0.85) {
      botScore += 2.5;
      criticalHit = true;
    } else if (critRoll < 0.08) {
      botScore -= 1.5;
    }

    if (botPrediction === attackRoll) {
      botScore += 1.5;
    }
    if (defenderPrediction === defenseRoll) {
      defenderScore += 1.5;
    }

    const success = botScore >= defenderScore;
    const targetIdx = getIndex(attack.to.x, attack.to.y);
    const attackerIdx = getIndex(attack.from.x, attack.from.y);

    if (addBattle && room.players[botIndex] && room.players[defenderIndex]) {
      addBattle({
        from: attack.from,
        to: attack.to,
        success: success,
        criticalHit: criticalHit,
        attackerColor: room.players[botIndex].color,
        defenderColor: room.players[defenderIndex].color
      });
    }

    const newGrid = [...gs.grid];

    if (success) {
      playSound('success', 0.5);
      newGrid[targetIdx] = botIndex;
      if (criticalHit) {
        playSound('critical-hit', 0.7);
      }
    } else {
      playSound('failure', 0.5);
      newGrid[attackerIdx] = defenderIndex;
    }

    const newAttackCount = (gs.attackCount || 0) + 1;

    let resultMessage = botScore > defenderScore ? 'KAZANDI' :
      botScore === defenderScore ? 'BERABERE - Saldırgan avantajı' :
        'KAYBETTI ve karesini kaybetti!';

    const updatedStats = updateBotStatsAfterBattle(
      botIndex,
      defenderIndex,
      success ? botScore * 0.7 : botScore * 0.3,
      success ? defenderScore * 0.7 : defenderScore * 1.2,
      gs.playerStats || {}
    );

    const newGameState = {
      ...gs,
      grid: newGrid,
      attackCount: newAttackCount,
      playerStats: updatedStats,
      log: [...gs.log, `🤖 ${room.players[botIndex].name}: (${attack.from.x},${attack.from.y}) → (${attack.to.x},${attack.to.y}) ⚔️ ${resultMessage}${criticalHit ? ' 💥' : ''} (🎲${attackRoll}-${defenseRoll})`]
    };

    try {
      await runTransaction(db, async (transaction) => {
        const roomDoc = await transaction.get(roomRef);
        if (!roomDoc.exists()) {
          throw new Error("Oda bulunamadı!");
        }

        // Kazanma kontrolü
        const winnerId = checkWinningConditions(newGrid, room.players, gs.turn);
        if (winnerId !== null) {
          newGameState.winner = winnerId;
          transaction.update(roomRef, { gameState: newGameState });
          return;
        }

        // Max turna ulaştı mı?
        if (gs.turn >= MAX_TURNS) {
          const territories = room.players.map((_, i) =>
            newGrid.filter(c => c === i).length
          );
          const maxTerritory = Math.max(...territories);
          const turnWinnerId = territories.indexOf(maxTerritory);
          newGameState.winner = turnWinnerId;
          transaction.update(roomRef, { gameState: newGameState });
          return;
        }

        // ===== ANAHTAR KOD BURASI =====
        if (gs.turn === 1) {
          const maxAttacksFirstTurn = 2;
          const hasMoreAttacks = newAttackCount < maxAttacksFirstTurn;
          const hasExpandLeft = !gs.hasExpanded;

          if (hasMoreAttacks || hasExpandLeft) {
            transaction.update(roomRef, { gameState: newGameState });
          } else {
            const nextPlayer = findNextActivePlayer(botIndex, newGrid, room.players.length);
            newGameState.currentPlayerIndex = nextPlayer;
            newGameState.hasExpanded = false;
            newGameState.attackCount = 0;

            if (nextPlayer === 0) {
              newGameState.turn = gs.turn + 1;
            }

            transaction.update(roomRef, { gameState: newGameState });
          }
        } else {

          const maxAttacks = MAX_ATTACKS_PER_TURN;
          const hasMoreAttacks = newAttackCount < maxAttacks;
          const hasExpandLeft = !gs.hasExpanded;

          if (hasMoreAttacks || hasExpandLeft) {

            transaction.update(roomRef, { gameState: newGameState });
          } else {
            const nextPlayer = findNextActivePlayer(botIndex, newGrid, room.players.length);
            newGameState.currentPlayerIndex = nextPlayer;
            newGameState.hasExpanded = false;
            newGameState.attackCount = 0;

            if (nextPlayer === 0) {
              newGameState.turn = gs.turn + 1;
            }

            transaction.update(roomRef, { gameState: newGameState });
          }
        }
      });

    } catch (error) {
      console.error('Bot saldırı hatası:', error);
    }
  }, [getIndex, rollDice, calculatePowerBonus, updateBotStatsAfterBattle, checkWinningConditions, addBattle, playSound, MAX_ATTACKS_PER_TURN, MAX_TURNS, findNextActivePlayer]);

  const botExpand = useCallback(async (roomCode, room, botIndex, move, gs) => {
    const roomRef = doc(db, 'rooms', roomCode);
    const targetIdx = getIndex(move.to.x, move.to.y);
    const newGrid = [...gs.grid];
    newGrid[targetIdx] = botIndex;
    playSound('expand', 0.5);

    const newGameState = {
      ...gs,
      grid: newGrid,
      hasExpanded: true,
      playerStats: gs.playerStats || {},
      log: [...gs.log, `🤖 ${room.players[botIndex].name}: (${move.from.x},${move.from.y}) → (${move.to.x},${move.to.y}) 📦 YAYILDI`]
    };

    try {
      await updateDoc(roomRef, { gameState: newGameState });
    } catch (error) {
      console.error('Bot genişleme hatası:', error);
    }
  }, [getIndex, playSound]);

  // ===== BOT TUR BİTİR (ÖNCE TANIMLANDI) =====
  // ===== BOT TUR BİTİR (ÖNCEDENTANIMLANDı) =====
  const botEndTurn = useCallback(async (room) => {
    const gs = room.gameState;
    const currentPlayers = room.players;

    if (!gs || !currentPlayers) return;

    playSound('click', 0.2);
    const roomRef = doc(db, 'rooms', roomCode);

    const nextPlayer = findNextActivePlayer(gs.currentPlayerIndex, gs.grid, currentPlayers.length);
    const nextTurn = nextPlayer === 0 ? gs.turn + 1 : gs.turn;

    // ⭐ ÇÖZÜM: nextTurn'u hesapla ve kontrol et
    const finalTurn = nextTurn;

    const newGameState = {
      ...gs,
      currentPlayerIndex: nextPlayer,
      turn: finalTurn,
      hasExpanded: false,
      attackCount: 0,
      playerStats: gs.playerStats || {}
    };

    // ⭐ MAX_TURNS kontrolü (tur sayısını geçtiyse oyun bitir)
    if (finalTurn >= MAX_TURNS && finalTurn > 0) {
      const territories = currentPlayers.map((_, i) =>
        gs.grid.filter(c => c === i).length
      );
      const maxTerritory = Math.max(...territories);
      const winnerId = territories.indexOf(maxTerritory);

      newGameState.winner = currentPlayers[winnerId].id;
      newGameState.log = [
        ...gs.log,
        `🏆 ${currentPlayers[winnerId].name} oyunu kazandı! (${maxTerritory} kare ile, Tur limiti (${MAX_TURNS}) sonunda!)`
      ];
    }

    try {
      await updateDoc(roomRef, { gameState: newGameState });
    } catch (error) {
      console.error('Bot tur bitirme hatası:', error);
    }
  }, [roomCode, playSound, findNextActivePlayer, MAX_TURNS]);

  // ===== BOT SETUP =====
  const executeBotSetup = useCallback(async (botIndex, room) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      const gs = room.gameState;
      const currentPlayers = room.players;

      if (!roomCode || !gs) return;

      await new Promise(resolve => setTimeout(resolve, 1500));

      const emptyCells = [];
      const gridLength = GRID_W * GRID_H;

      for (let i = 0; i < gridLength; i++) {
        if (gs.grid[i] === -1) {
          emptyCells.push(i);
        }
      }

      if (emptyCells.length === 0) return;

      const selectedCellIndices = [];
      const clusterCandidates = [];

      const firstCellIdx = emptyCells.splice(Math.floor(Math.random() * emptyCells.length), 1)[0];
      selectedCellIndices.push(firstCellIdx);

      const getNeighbors = (index) => {
        const x = index % GRID_W;
        const y = Math.floor(index / GRID_W);
        const neighbors = [];

        if (x > 0) neighbors.push(index - 1);
        if (x < GRID_W - 1) neighbors.push(index + 1);
        if (y > 0) neighbors.push(index - GRID_W);
        if (y < GRID_H - 1) neighbors.push(index + GRID_W);

        return neighbors;
      };

      getNeighbors(firstCellIdx).forEach(neighborIdx => {
        const indexInEmpty = emptyCells.indexOf(neighborIdx);
        if (indexInEmpty !== -1) {
          clusterCandidates.push(neighborIdx);
          emptyCells.splice(indexInEmpty, 1);
        }
      });

      for (let i = 0; i < INITIAL_CELLS - 1; i++) {
        let chosenIdx;

        if (clusterCandidates.length > 0) {
          chosenIdx = clusterCandidates.splice(Math.floor(Math.random() * clusterCandidates.length), 1)[0];
        } else if (emptyCells.length > 0) {
          chosenIdx = emptyCells.splice(Math.floor(Math.random() * emptyCells.length), 1)[0];
        } else {
          break;
        }

        selectedCellIndices.push(chosenIdx);

        getNeighbors(chosenIdx).forEach(neighborIdx => {
          const indexInEmpty = emptyCells.indexOf(neighborIdx);
          if (indexInEmpty !== -1 && !clusterCandidates.includes(neighborIdx)) {
            clusterCandidates.push(neighborIdx);
            emptyCells.splice(indexInEmpty, 1);
          }
        });
      }

      const roomRef = doc(db, 'rooms', roomCode);
      const newGrid = [...gs.grid];

      selectedCellIndices.forEach(index => {
        newGrid[index] = botIndex;
      });

      const nextPlayer = (gs.currentPlayerIndex + 1) % currentPlayers.length;
      const allCellsChosen = newGrid.filter(c => c >= 0).length >= currentPlayers.length * INITIAL_CELLS;

      const newGameState = {
        ...gs,
        grid: newGrid,
        currentPlayerIndex: nextPlayer,
        playerStats: gs.playerStats || {},
        log: [...gs.log, `🤖 ${currentPlayers[botIndex].name} ${selectedCellIndices.length} karesini seçti.`]
      };

      const updates = { gameState: newGameState };

      if (allCellsChosen) {
        newGameState.turn = 1;
        newGameState.hasExpanded = false;
        newGameState.attackCount = 0;
        newGameState.log.push('🎲 Oyun Başladı! Tur 1.');
        updates.status = 'playing';
      }

      await updateDoc(roomRef, updates);
    } catch (error) {
      console.error('Bot setup hatası:', error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [roomCode, INITIAL_CELLS]);

  const executeBotAction = useCallback(async (botIndex, room) => {
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;

    try {
      const gs = room.gameState;
      const currentPlayers = room.players;

      if (!roomCode || !gs || gs.currentPlayerIndex !== botIndex) {
        isProcessingRef.current = false;
        return;
      }

      const maxAttacks = gs.turn === 1 ? 2 : MAX_ATTACKS_PER_TURN;

      // Hamle hakları kontrolü
      const hasAttackLeft = (gs.attackCount || 0) < maxAttacks;
      const hasExpandLeft = !gs.hasExpanded;
      if (!hasAttackLeft && !hasExpandLeft) {
        await new Promise(resolve => setTimeout(resolve, 800));
        await botEndTurn(room);
        isProcessingRef.current = false;
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1200));
      playSound('click', 0.3);

      // Botun hücrelerini bul
      const botCells = [];
      const attackTargets = [];
      const expandTargets = [];
      const weakTargets = [];
      const gridLength = GRID_W * GRID_H;

      for (let i = 0; i < gridLength; i++) {
        if (gs.grid[i] === botIndex) {
          const x = i % GRID_W;
          const y = Math.floor(i / GRID_W);
          botCells.push({ x, y, index: i });
        }
      }

      if (botCells.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 800));
        await botEndTurn(room);
        isProcessingRef.current = false;
        return;
      }

      const botStats = gs.playerStats?.[botIndex] || { attackPower: 0, defensePower: 0 };
      const isWeakBot = botStats.attackPower <= 1;

      // Tüm hedefleri bul
      for (let i = 0; i < botCells.length; i++) {
        const cell = botCells[i];
        const x = cell.x;
        const y = cell.y;

        // Komşu hücreleri kontrol et
        if (x > 0) processCell({ x: x - 1, y }, gs.grid[(y * GRID_W) + (x - 1)], cell, attackTargets, expandTargets, weakTargets, gs, botIndex);
        if (x < GRID_W - 1) processCell({ x: x + 1, y }, gs.grid[(y * GRID_W) + (x + 1)], cell, attackTargets, expandTargets, weakTargets, gs, botIndex);
        if (y > 0) processCell({ x, y: y - 1 }, gs.grid[((y - 1) * GRID_W) + x], cell, attackTargets, expandTargets, weakTargets, gs, botIndex);
        if (y < GRID_H - 1) processCell({ x, y: y + 1 }, gs.grid[((y + 1) * GRID_W) + x], cell, attackTargets, expandTargets, weakTargets, gs, botIndex);
      }

      // Hedef işleme fonksiyonu
      function processCell(adj, owner, cell, attackTargets, expandTargets, weakTargets, gs, botIndex) {
        if (owner >= 0 && owner !== botIndex) {
          const targetStats = gs.playerStats?.[owner] || { defensePower: 0 };
          const enemyTerritory = gs.grid.filter(c => c === owner).length;

          if (enemyTerritory <= 2 || targetStats.defensePower <= 1) {
            weakTargets.push({
              from: { x: cell.x, y: cell.y },
              to: adj,
              owner: owner,
              defensePower: targetStats.defensePower
            });
          } else {
            attackTargets.push({
              from: { x: cell.x, y: cell.y },
              to: adj,
              owner: owner,
              defensePower: targetStats.defensePower
            });
          }
        } else if (owner === -1) {
          expandTargets.push({
            from: { x: cell.x, y: cell.y },
            to: adj
          });
        }
      }

      // SALDIRI SEÇENEĞİ (Genişlemeden önce!)
      if (hasAttackLeft) {
        let targets = weakTargets.length > 0 ? weakTargets : attackTargets;

        if (targets.length > 0) {
          targets.sort((a, b) => a.defensePower - b.defensePower);

          await new Promise(resolve => setTimeout(resolve, 800));

          let selectedAttack = targets[0];
          if (isWeakBot && Math.random() > 0.3 && targets.length > 1) {
            selectedAttack = targets[Math.floor(Math.random() * Math.min(3, targets.length))];
          }

          await botAttack(roomCode, room, botIndex, selectedAttack, gs);

          return; // Sadece return, process durması yok
        }
      }

      // GENİŞLEME SEÇENEĞİ (Saldırı yoksa)
      if (hasExpandLeft && expandTargets.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 600));
        const selectedExpand = expandTargets[Math.floor(Math.random() * expandTargets.length)];
        await botExpand(roomCode, room, botIndex, selectedExpand, gs);

        // ⭐ ÖNEMLİ: BURADA isProcessingRef = false YAPMA!

        return; // Sadece return, process durması yok
      }

      // Hiçbir hamle yapılamadıysa, turu bitir
      await new Promise(resolve => setTimeout(resolve, 800));
      await botEndTurn(room);

    } catch (error) {
      console.error('Bot hareket hatası:', error);
    } finally {
      isProcessingRef.current = false;  // ⭐ SADECE BURADA
    }
  }, [roomCode, playSound, MAX_ATTACKS_PER_TURN, GRID_W, GRID_H, botAttack, botExpand, botEndTurn]);

  useEffect(() => {
    return () => {
      if (botTimeoutRef.current) {
        clearTimeout(botTimeoutRef.current);
      }
      isProcessingRef.current = false;
    };
  }, []);

  return {
    executeBotSetup,
    executeBotAction
  };
};